import { useEffect } from 'react'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { useLoaderData } from 'react-router-dom'
import useNotificationsSubscriber from '@/routes/projectStages/notifications/lib/hooks/useNotificationsSubscriber.tsx'
import useCreateStageFilters from '@/routes/root/lib/hooks/useCreateStageFilters.tsx'
import { RootLoaderT } from '@/routes/root/lib/actions/rootLoader.ts'
import { currStageIssueTypesQuery } from '@/lib/actions/getStageIssuesTypes.ts'
import { useQuery } from '@tanstack/react-query'
import { currStageQuery } from '@/lib/actions/getCurrStage.ts'
import { notificationCountsQuery } from '@/routes/projectStages/notifications/lib/actions/notificationsActions.ts'
import { shouldClearActiveProjectStage } from '@/routes/root/lib/hooks/stageAccessError.ts'
import { clearActiveProjectStageOnce } from '@/routes/root/lib/hooks/clearActiveProjectStage.ts'
import { useUserStore } from '@/store/user.ts'

type PropsT = {
  path: 'Root' | 'ProjectStage'
  disabled?: boolean
}

const useProjectStageData = ({ disabled }: PropsT) => {
  // console.log({ path })
  const data = useLoaderData() as Awaited<RootLoaderT>

  const { stageId, stageUsers, stageIssueTypes, stage, userId } = data ?? {}

  const { data: stageIssueTypesQuery, error: stageIssueTypesError } = useQuery({
    ...currStageIssueTypesQuery(stageId as string),
    enabled: !!stageId,
    throwOnError: false,
  })

  const { data: stageQuery, error: stageQueryError } = useQuery({
    ...currStageQuery(stageId as string, userId as number),
    enabled: !!stageId,
    throwOnError: false,
  })

  // Authoritative global inbox badge: the counts endpoint is the single source
  // of truth for the mail/Skrzynka total (all unread notifications, incl. those
  // for archived / role-hidden issues). It must NOT be derived from the per-type
  // sidebar badges, which count only list-visible issues (blue dots). The SSE
  // subscriber keeps it live; this seeds it on load / stage change.
  const { data: notificationCounts } = useQuery({
    ...notificationCountsQuery(stageId as string),
    enabled: !!stageId,
    throwOnError: false,
  })

  // Drop the persisted active stage only when the stage the user has SELECTED is
  // really gone or no longer visible to them (403/404). Any other failure — a
  // gateway timeout, a 5xx during a deploy, a flaky mobile connection — says
  // nothing about the stage, and clearing it on those throws the user out of the
  // stage on the next load. `rootLoader` reaches the same conclusion for a cold
  // cache; both funnel through `clearActiveProjectStageOnce`, which is what keeps
  // one failure from producing several PATCHes (this hook is mounted twice on a
  // stage route: `Root` and `ProjectsRoot`).
  const persistedActiveStage = useUserStore(state => state.user?.activeProjectStage)
  useEffect(() => {
    if (!userId) return
    if (
      !shouldClearActiveProjectStage({
        persistedActiveStage,
        failedStageId: stageId,
        errors: [stageQueryError, stageIssueTypesError],
      })
    )
      return
    void clearActiveProjectStageOnce(userId).catch(console.error)
  }, [persistedActiveStage, stageId, stageQueryError, stageIssueTypesError, userId])

  const setCurrProjectStage = useProjectStageStore(state => state.setCurrProjectStage)
  const setIssueTypes = useProjectStageStore(state => state.setIssueTypes)
  const setAllUnreadNotificationsCount = useProjectStageStore(
    state => state.setAllUnreadNotificationsCount,
  )

  useEffect(() => {
    if (disabled) return
    if (notificationCounts) setAllUnreadNotificationsCount(notificationCounts.inbox)
  }, [disabled, notificationCounts, setAllUnreadNotificationsCount])

  // Hydrate from loader once
  useEffect(() => {
    if (disabled) return
    if (stage) setCurrProjectStage(stage)
    if (stageIssueTypes) setIssueTypes(stageIssueTypes)
  }, [disabled, stage, stageIssueTypes, setCurrProjectStage, setIssueTypes])

  // Update from React Query whenever it changes
  useEffect(() => {
    if (disabled) return
    if (stageQuery) setCurrProjectStage(stageQuery)
    if (stageIssueTypesQuery) setIssueTypes(stageIssueTypesQuery)
  }, [disabled, stageQuery, stageIssueTypesQuery, setCurrProjectStage, setIssueTypes])

  // useUpdateStageTabHref()
  useNotificationsSubscriber(stageUsers, stageId)
  useCreateStageFilters(stageUsers, stageId)
}

export default useProjectStageData
